import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  MaintenanceContext,
  MAINTENANCE_DEFAULT_STATE,
  type MaintenanceState,
  type GracePhase,
  type ConnectionMode,
} from "@/contexts/MaintenanceContext";
import { useWebSocket, WebSocketStatus } from "@/hooks/useWebSocket";
import { maintenanceWindowApi } from "@/services/maintenanceWindowApi";
import { useAuth } from "@/contexts/AuthContext";
import type { MaintenanceLevel, MaintenanceScope } from "@/types/maintenance-window";
import {
  calcularFaseDaCarencia,
  normalizarFaseDaCarencia,
  segundosRestantesAte,
} from "@/modules/maintenance/utils/gracePeriodPhase";
import { MaintenanceOverlay } from "@/modules/maintenance/components/MaintenanceOverlay";
import { useMaintenanceAutoSave } from "@/modules/maintenance/hooks/useMaintenanceAutoSave";
import { useStatePreservation } from "@/modules/maintenance/hooks/useStatePreservation";
import { usePostMaintenanceRestore } from "@/modules/maintenance/hooks/usePostMaintenanceRestore";

import { getWsUrl } from "@/config/api";
import { soltarDonoDoPush } from "@/lib/push/donoDoPush";

// Runtime URL via getWsUrl() — em prod resolve por window.location.host
// (resiliente a mudança de IP da VM); em dev usa VITE_WS_URL.
// getWsUrl() não prefixa /api/v1 (outros consumidores como equipment/terminals
// já tinham esse bug), então passamos o path completo aqui.
const WS_URL = getWsUrl("/api/v1/ws/maintenance");

interface MaintenanceWsEvent {
  type: "active" | "countdown" | "ended" | "warning" | "logout_forced" | "scheduled_reminder";
  level?: number | string;
  grace_period_ends_at?: string;
  modules?: string[];
  message?: string;
  estimated_end?: string;
  seconds_remaining?: number;
  phase?: string;
  minutes_until?: number;
  scheduled_at?: string;
  estimated_duration_minutes?: number;
  emergency?: boolean;
  completed_at?: string;
}

function parseLevelValue(value: number | string | undefined): MaintenanceLevel | null {
  if (value === 1 || value === "level_1") return "level_1";
  if (value === 2 || value === "level_2") return "level_2";
  if (value === 3 || value === "level_3") return "level_3";
  return null;
}

export const MaintenanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MaintenanceState>(MAINTENANCE_DEFAULT_STATE);
  const stateRef = useRef(state);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Keep ref in sync for stable callbacks
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Story 3.5: Forced logout helper
  const _performForcedLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // É um fim da sessão interna: a marca de dono do push do aparelho sai junto (se for do interno), senão o parceiro do portal no
    // mesmo navegador não toma o push e os avisos do colaborador seguem chegando ali (decisão de 12/10/2026).
    soltarDonoDoPush("interno");
    sessionStorage.clear();
    window.location.href = "/maintenance";
  }, []);

  // --- WebSocket ---
  const handleWsMessage = useCallback(
    (data: MaintenanceWsEvent) => {
      switch (data.type) {
        case "active": {
          // Achado ALTO (revisão de segurança + E2E, 28/09/2026): este
          // handler gravava phase:null, e manutencaoEstaTravada (versão
          // antiga) tratava null como travado — o não-admin caía em
          // /maintenance DURANTE a carência (toda ativação tem carência
          // >=10s). Agora calcula a fase de verdade a partir do
          // grace_period_ends_at do próprio payload, igual ao que o
          // polling/status já faz.
          const segundosRestantes = segundosRestantesAte(data.grace_period_ends_at ?? null);
          const faseInicial =
            segundosRestantes !== null ? calcularFaseDaCarencia(segundosRestantes) : null;
          setState((prev) => ({
            ...prev,
            isActive: true,
            level: parseLevelValue(data.level),
            scope: (data.modules && data.modules.length > 0
              ? "partial"
              : "total") as MaintenanceScope,
            message: data.message ?? null,
            estimatedEnd: data.estimated_end ?? null,
            affectedModules: data.modules ?? [],
            gracePeriodEndsAt: data.grace_period_ends_at ?? null,
            phase: faseInicial,
            isReadOnly: faseInicial === "readonly" || faseInicial === "logout",
            isEmergency: data.emergency ?? false,
          }));
          queryClient.invalidateQueries({ queryKey: ["maintenance-status"] });
          // Story 5.3: WS active event doesn't include limited_modules.
          // Fetch status once to populate limitedModules (polling may be disabled when WS is connected).
          if (data.modules && data.modules.length > 0) {
            maintenanceWindowApi
              .getStatus()
              .then((statusData) => {
                const newLimited = statusData.limited_modules ?? [];
                setState((prev) => ({ ...prev, limitedModules: newLimited }));
              })
              .catch(() => {
                /* best-effort — will sync on next poll */
              });
          }
          break;
        }

        case "countdown":
          setState((prev) => {
            const newPhase = (data.phase as GracePhase) ?? prev.phase;
            return {
              ...prev,
              phase: newPhase,
              gracePeriodEndsAt: prev.gracePeriodEndsAt,
              isReadOnly: newPhase === "readonly" || newPhase === "logout",
            };
          });
          // Story 3.5 AC3: If countdown reaches 0 with phase "logout", trigger forced logout
          if (data.phase === "logout" && data.seconds_remaining === 0) {
            _performForcedLogout();
          }
          break;

        case "logout_forced":
          // Story 3.5 AC3: Server forced session invalidation
          _performForcedLogout();
          break;

        case "ended": {
          setState((prev) => ({
            ...prev,
            isActive: false,
            level: null,
            scope: null,
            message: data.message ?? null,
            estimatedEnd: null,
            affectedModules: [],
            gracePeriodEndsAt: null,
            phase: null,
            isReadOnly: false,
            isEmergency: false,
            limitedModules: [],
          }));
          queryClient.invalidateQueries({ queryKey: ["maintenance-status"] });
          // Story 3.7: Set recentlyEnded flag for post-maintenance banner
          const now = new Date();
          const recentlyEndedFlag = {
            timestamp: now.toISOString(),
            expiresAt: new Date(now.getTime() + 30 * 60_000).toISOString(),
          };
          try {
            localStorage.setItem(
              "seweb:maintenance:recentlyEnded",
              JSON.stringify(recentlyEndedFlag)
            );
          } catch {
            /* localStorage unavailable */
          }
          break;
        }

        case "warning":
          setState((prev) => ({
            ...prev,
            message: data.message ?? prev.message,
            affectedModules: data.modules ?? prev.affectedModules,
            phase: "warn",
          }));
          break;

        case "scheduled_reminder": {
          const incomingScheduledAt = data.scheduled_at ?? "";
          const dismissed = sessionStorage.getItem("dismissed_scheduled_banner");
          if (dismissed === incomingScheduledAt) break; // already dismissed this session
          setState((prev) => ({
            ...prev,
            scheduledMaintenance: {
              scheduledAt: incomingScheduledAt,
              level: parseLevelValue(data.level) ?? "level_1",
              message: data.message ?? null,
              estimatedDurationMinutes: data.estimated_duration_minutes ?? null,
            },
          }));
          break;
        }
      }
    },
    [queryClient, _performForcedLogout]
  );

  const { status: wsStatus } = useWebSocket({
    url: WS_URL,
    onMessage: handleWsMessage,
    reconnect: true,
    reconnectInterval: 5000,
    maxReconnectAttempts: 5,
    autoConnect: true,
  });

  // --- Connection mode tracking ---
  const wsConnected = wsStatus === WebSocketStatus.CONNECTED;
  const wsDisconnected =
    wsStatus === WebSocketStatus.DISCONNECTED || wsStatus === WebSocketStatus.ERROR;

  // --- Polling fallback ---
  const pollingInterval = useMemo(() => {
    if (wsConnected) return 30_000; // WS connected: poll every 30s as sync fallback
    if (state.isActive) return 5_000; // Active maintenance, WS down: 5s
    return 30_000; // No maintenance, WS down: 30s (NFR7)
  }, [wsConnected, state.isActive]);

  const { data: pollingData } = useQuery({
    queryKey: ["maintenance-status"],
    queryFn: maintenanceWindowApi.getStatus,
    refetchInterval: pollingInterval,
    // Pausa durante a troca de senha obrigatória: o gate server-side 403a toda
    // rota protegida até a senha ser trocada (evita poll de 403 no console).
    enabled: !user?.password_change_required,
  });

  // Story 4.3 AC4: Check for recent_maintenance on mount (login after maintenance)
  useEffect(() => {
    if (!pollingData) return;
    if (!pollingData.is_active && pollingData.recent_maintenance) {
      const completedAt = new Date(pollingData.recent_maintenance.completed_at).getTime();
      const flag = {
        timestamp: pollingData.recent_maintenance.completed_at,
        expiresAt: new Date(completedAt + 30 * 60_000).toISOString(),
      };
      try {
        const existing = localStorage.getItem("seweb:maintenance:recentlyEnded");
        if (!existing) {
          localStorage.setItem("seweb:maintenance:recentlyEnded", JSON.stringify(flag));
        }
      } catch {
        /* localStorage unavailable */
      }
    }
  }, [pollingData]);

  // Sync polling data into state
  useEffect(() => {
    if (!pollingData) return; // Sync polling data regardless of WS state
    setState((prev) => {
      const newIsActive = pollingData.is_active;
      const newLevel = pollingData.level ?? null;
      const newScope = pollingData.scope ?? null;
      const newMessage = pollingData.message ?? null;
      const newEstimatedEnd = pollingData.estimated_end ?? null;
      const newModules = pollingData.modules ?? [];
      const newGracePeriodEndsAt = pollingData.grace_period_ends_at ?? null;
      // "expired" (backend manda quando a carência já passou) não é uma fase
      // de carência de verdade — vira null aqui, não fica truthy à toa.
      const newPhase = normalizarFaseDaCarencia(pollingData.phase);
      const newLimitedModules = pollingData.limited_modules ?? [];
      // Contrato item 3 (28/09/2026): reflete o PAPEL do usuário do token
      // (independe de is_active), então precisa entrar na comparação de
      // baixo — senão uma mudança SÓ nele (ex.: permissão revogada no meio
      // de uma manutenção já ativa) ficaria presa no `return prev`.
      const newPodeOperar = pollingData.pode_operar ?? null;

      // Avoid unnecessary updates
      const limitedChanged =
        prev.limitedModules.length !== newLimitedModules.length ||
        prev.limitedModules.some((m, i) => m.id !== newLimitedModules[i]?.id);
      if (
        prev.isActive === newIsActive &&
        prev.level === newLevel &&
        prev.scope === newScope &&
        prev.message === newMessage &&
        prev.estimatedEnd === newEstimatedEnd &&
        prev.gracePeriodEndsAt === newGracePeriodEndsAt &&
        prev.phase === newPhase &&
        prev.podeOperar === newPodeOperar &&
        !limitedChanged
      ) {
        return prev;
      }
      return {
        ...prev,
        isActive: newIsActive,
        level: newLevel,
        scope: newScope,
        message: newMessage,
        estimatedEnd: newEstimatedEnd,
        affectedModules: newModules,
        gracePeriodEndsAt: newGracePeriodEndsAt,
        phase: newPhase,
        isReadOnly: newPhase === "readonly" || newPhase === "logout",
        limitedModules: newLimitedModules,
        podeOperar: newPodeOperar,
      };
    });
  }, [pollingData, wsConnected]);

  // --- Connection mode ---
  useEffect(() => {
    const mode: ConnectionMode = wsConnected
      ? "websocket"
      : wsDisconnected
        ? "polling"
        : "disconnected";
    setState((prev) => (prev.connectionMode === mode ? prev : { ...prev, connectionMode: mode }));
  }, [wsConnected, wsDisconnected, pollingInterval]);

  // --- Story 3.6: Auto-save and state preservation hooks ---
  const { saveStatus, registerDirtyForm, unregisterForm } = useMaintenanceAutoSave();
  useStatePreservation();

  // --- Story 3.7: Post-maintenance restoration ---
  const {
    showReturnBanner,
    hasDraft,
    draftCount,
    draftRoute,
    draftTimestamp,
    restoreDraft,
    dismissBanner,
    dismissDraft,
    reopenBanner,
  } = usePostMaintenanceRestore();

  // Sync saveStatus into state
  useEffect(() => {
    setState((prev) => (prev.saveStatus === saveStatus ? prev : { ...prev, saveStatus }));
  }, [saveStatus]);

  // Story 4.2: Dismiss scheduled maintenance banner (persists per session)
  const dismissScheduledBanner = useCallback(() => {
    setState((prev) => {
      if (prev.scheduledMaintenance?.scheduledAt) {
        sessionStorage.setItem("dismissed_scheduled_banner", prev.scheduledMaintenance.scheduledAt);
      }
      return { ...prev, scheduledMaintenance: null };
    });
  }, []);

  // --- Memoized context value (AC5: zero overhead via useMemo) ---
  const contextValue = useMemo(
    () => ({
      ...state,
      isModuleAffected: (moduleId: string) =>
        state.affectedModules.some((m) => m.toLowerCase() === moduleId.toLowerCase()),
      registerDirtyForm,
      unregisterForm,
      dismissScheduledBanner,
      showReturnBanner,
      hasDraft,
      draftCount,
      draftRoute,
      draftTimestamp,
      restoreDraft,
      dismissBanner,
      dismissDraft,
      reopenBanner,
    }),
    [
      state,
      registerDirtyForm,
      unregisterForm,
      dismissScheduledBanner,
      showReturnBanner,
      hasDraft,
      draftCount,
      draftRoute,
      draftTimestamp,
      restoreDraft,
      dismissBanner,
      dismissDraft,
      reopenBanner,
    ]
  );

  return (
    <MaintenanceContext.Provider value={contextValue}>
      <MaintenanceOverlay />
      {children}
    </MaintenanceContext.Provider>
  );
};
